import type { ContentLang } from '@/features/editor/editorSlice'
import { resolveLang } from '@/features/editor/components/studio/seedContentLang'

/**
 * Ключи целей строятся из id, что лежат в `course.lessons`, — у живого курса с
 * черновиком это id ЧЕРНОВИКА, а старт идёт по id живого курса. Так и надо:
 * резолвер бэка (`map_target_keys`) принимает id черновика как свои.
 */

/**
 * Слот картинки — это ИМЯ КОЛОНКИ, а не язык (§5.3): `base` ↔ `imagePrompt`,
 * `en` ↔ `imagePromptEn`. У английского курса базовая колонка всё равно
 * `imagePrompt`, поэтому `lesson:50:ru` — мусор, за который бэк отдаёт `422`.
 */
export type ImageSlot = 'base' | 'en'

/** Слот → язык в терминах `readPrompt`/`promptKey` (`ru` = базовая колонка). */
export const slotLang = (slot: ImageSlot): ContentLang => (slot === 'base' ? 'ru' : 'en')

/**
 * Язык → слот: обратная сторона `slotLang`, а НЕ признак «основной/второй».
 * Какой слот исходный, решает язык оригинала курса (`seedLangFor`): у
 * английского курса оригинал лежит в колонке `*En`, то есть его исходный слот —
 * `en`, а второй — `base`. Незнакомое и отсутствующее значение (в том числе
 * `'-'` у джобы без языка) сводится к `ru` тем же реестром `resolveLang`.
 */
export const langSlot = (lang: string | null | undefined): ImageSlot =>
  resolveLang(lang) === 'en' ? 'en' : 'base'

/** Парный слот: их ровно два, и второй — всегда не этот. */
export const otherSlot = (slot: ImageSlot): ImageSlot => (slot === 'base' ? 'en' : 'base')

// Ключи собираются ТОЛЬКО из id сущностей: названия, заголовки и slug в них не
// участвуют — они меняются, а ключ обязан указывать на строку в базе.
export const bannerKey = (courseId: number): string => `course_banner:${courseId}`
export const mascotKey = (mascotId: number): string => `mascot:${mascotId}`
export const lessonKey = (lessonId: number, slot: ImageSlot): string => `lesson:${lessonId}:${slot}`
export const blockKey = (blockId: number, slot: ImageSlot): string => `block:${blockId}:${slot}`

/**
 * Первый id — БЛОКА, id элемента идёт последним и разбирается бэком как весь
 * остаток строки, поэтому двоеточие внутри него законно и экранировать его
 * нельзя (`element:440:base:hero:2` — элемент `hero:2`).
 */
export const elementKey = (blockId: number, slot: ImageSlot, elementId: string): string =>
  `element:${blockId}:${slot}:${elementId}`

/**
 * Порядок в теле запроса: сначала базовые цели, потом `:en` (§5.3). Если
 * базовой картинки нет, цель второго языка пропускается — при смешанном списке
 * порядок решает, будет ли `:en` чем рисовать. Внутри группы порядок автора
 * сохраняется: сортировка стабильная.
 */
export const sortTargetKeys = (keys: string[]): string[] => [
  ...keys.filter((key) => !key.endsWith(':en')),
  ...keys.filter((key) => key.endsWith(':en')),
]
